import { useQuery, useMutation } from "@apollo/client";
import {
  ORGANIZATION_ROSTER,
  ORGANIZATION_ROSTER_STATS,
  ORGANIZATION_SEAT_USAGE,
  BULK_IMPORT_USERS,
  SEND_ROSTER_INVITES,
} from "../graphql";
import type {
  RosterResult,
  RosterStats,
  RosterEnrollmentStatus,
  BulkImportReport,
  SendInvitesReport,
} from "../types";
import type { SeatUsage } from "../../organizations/types";

// ─── Queries ─────────────────────────────────────────────────────────────────

interface UseRosterOptions {
  organizationId: string | null;
  limit?: number;
  offset?: number;
  search?: string;
  status?: RosterEnrollmentStatus;
}

export function useOrganizationRoster({
  organizationId,
  limit = 20,
  offset = 0,
  search,
  status,
}: UseRosterOptions) {
  return useQuery<{ organizationRoster: RosterResult }>(ORGANIZATION_ROSTER, {
    variables: {
      organizationId,
      input: {
        limit,
        offset,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
      },
    },
    skip: !organizationId,
    fetchPolicy: "cache-and-network",
  });
}

export function useOrganizationRosterStats(organizationId: string | null) {
  return useQuery<{ organizationRosterStats: RosterStats }>(
    ORGANIZATION_ROSTER_STATS,
    {
      variables: { organizationId },
      skip: !organizationId,
      fetchPolicy: "cache-and-network",
    }
  );
}

export function useOrganizationSeatUsage(organizationId: string | null) {
  return useQuery<{ organization: { _id: string; seatUsage: SeatUsage } | null }>(
    ORGANIZATION_SEAT_USAGE,
    {
      variables: { id: organizationId },
      skip: !organizationId,
      fetchPolicy: "cache-and-network",
    }
  );
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useBulkImportUsers() {
  return useMutation<
    { bulkImportUsers: BulkImportReport },
    {
      organizationId: string;
      file: File;
      input?: {
        mode?: "ADD_ONLY" | "UPSERT";
        enrollExistingUsers?: boolean;
        updateExistingMembers?: boolean;
        dryRun?: boolean;
      };
    }
  >(BULK_IMPORT_USERS);
}

export function useSendRosterInvites() {
  return useMutation<
    { sendRosterInvites: SendInvitesReport },
    { organizationId: string; emails: string[] }
  >(SEND_ROSTER_INVITES);
}
